import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { isAuthRejection } from '@/db/application/auth'
import type { User } from '@/db/domain/models'
import { useDb } from '@/lib/dbContext'
import { getOrCreateKioskDeviceUuid } from '@/lib/kioskDevice'

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  login: (identity: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
  setUser: (user: User | null) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
)

export function AuthProvider(props: { children: ReactNode }) {
  const { children } = props

  const db = useDb()
  const [user, setUser] = useState<User | null>(() =>
    db.authStore.isValid ? db.getCurrentUser() : null,
  )

  const isAutoCancelError = (err: unknown) => {
    if (err instanceof Error) {
      const message = err.message.toLowerCase()
      if (message.includes('autocancelled') || message.includes('auto-cancelled')) {
        return true
      }
    }
    const cause = (err as { cause?: unknown })?.cause
    return cause instanceof Error && cause.name === 'AbortError'
  }

  const refreshSession = useCallback(async () => {
    if (!db.authStore.token) return
    try {
      const deviceUuid = db.authStore.user?.deviceUuid ? getOrCreateKioskDeviceUuid() : undefined
      const result = await db.refreshAuth(deviceUuid)
      setUser(result.user)
    } catch (err) {
      if (isAutoCancelError(err)) {
        return
      }
      // Only log out when the server explicitly rejected the session
      // (401/403). Transient failures — network down, 5xx — keep the
      // session so a startup check or background refresh during a Wi-Fi
      // blip doesn't silently log the user out (JC-095).
      if (!isAuthRejection(err)) {
        console.warn('Auth refresh failed transiently, keeping session', err)
        return
      }
      console.warn('Auth refresh rejected, clearing session', err)
      await db.logout()
      setUser(null)
    }
  }, [db])

  const login = useCallback(
    async (identity: string, password: string) => {
      const result = await db.loginWithPassword(identity, password)
      setUser(result.user)
    },
    [db],
  )

  const logout = useCallback(async () => {
    await db.logout()
    setUser(null)
  }, [db])

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refreshSession,
      setUser,
    }),
    [user, logout, login, refreshSession],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
